import React, { useState, useMemo } from 'react';

/**
 * QuizResultsHost — teacher's view of the finished battle. Replaces the
 * student-style "winner / podium" page with a class summary + per-question
 * review so the teacher can debrief the answers.
 */
function QuizResultsHost({
  finalScores,
  questions,
  hostKeys,
  hostAnswers,
  gameType,
  onPlayAgain,
  onBackToMenu,
}) {
  const [expandedQId, setExpandedQId] = useState(null);
  const [showAllPicks, setShowAllPicks] = useState(true);

  const formatTime = (ms) => {
    if (!ms) return '--';
    return `${(ms / 1000).toFixed(1)}s`;
  };

  // Class-level aggregates: average correct, average time, fastest student.
  const summary = useMemo(() => {
    if (!finalScores || finalScores.length === 0) return null;
    const total = finalScores[0]?.total || questions.length || 1;
    const correctSum = finalScores.reduce((acc, p) => acc + (p.correct || 0), 0);
    const finishedScores = finalScores.filter((p) => p.finished);
    const timeSum = finishedScores.reduce((acc, p) => acc + (p.finishMs || 0), 0);
    const fastest = finishedScores.length > 0
      ? finishedScores.reduce((min, p) => (p.finishMs < min.finishMs ? p : min))
      : null;
    return {
      total,
      avgCorrect: correctSum / finalScores.length,
      avgPercent: total > 0 ? (correctSum / (finalScores.length * total)) * 100 : 0,
      avgTimeMs: finishedScores.length > 0 ? timeSum / finishedScores.length : 0,
      finishedCount: finishedScores.length,
      fastest,
    };
  }, [finalScores, questions.length]);

  // For each question, count picks by option + identify who got it right.
  // Built once at game-end since hostAnswers stops updating after game_over.
  const questionStats = useMemo(() => {
    return questions.map((q) => {
      const correctId = hostKeys[q.questionId] || '';
      const picks = q.options.map((opt) => ({
        ...opt,
        isCorrect: opt.id === correctId,
        pickedBy: finalScores
          .filter((s) => hostAnswers[s.playerId]?.byQuestion?.[q.questionId]?.answerId === opt.id)
          .map((s) => s.playerName),
      }));
      const correctCount = finalScores.filter(
        (s) => hostAnswers[s.playerId]?.byQuestion?.[q.questionId]?.correct
      ).length;
      const answeredCount = finalScores.filter(
        (s) => hostAnswers[s.playerId]?.byQuestion?.[q.questionId]
      ).length;
      return { ...q, correctId, picks, correctCount, answeredCount };
    });
  }, [questions, hostKeys, hostAnswers, finalScores]);

  if (!finalScores || finalScores.length === 0) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: 'var(--paper)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <h1 className="t-display" style={{ marginBottom: 24 }}>No students finished</h1>
          <button className="btn" onClick={onBackToMenu}>Back to menu</button>
        </div>
      </div>
    );
  }

  const winner = finalScores[0];

  return (
    <div style={{ minHeight: '100vh', background: 'var(--paper)' }}>
      <header className="ink-header">
        <span
          className="t-mono"
          style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase' }}
        >
          Class results · 班级成绩
        </span>
        <span className="t-mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
          {finalScores.length} student{finalScores.length === 1 ? '' : 's'}
        </span>
      </header>

      <div
        style={{
          maxWidth: 1100,
          margin: '0 auto',
          padding: 'clamp(24px, 4vw, 40px) clamp(16px, 4vw, 32px)',
        }}
      >
        {/* Winner + class summary */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div
            style={{
              fontFamily: 'var(--font-han)',
              fontSize: 'clamp(56px, 10vw, 80px)',
              lineHeight: 1,
              color: 'var(--vermillion)',
              marginBottom: 12,
            }}
          >
            赛
          </div>
          <h1 className="t-display" style={{ marginBottom: 8 }}>
            {winner.playerName} wins
          </h1>
          <p className="t-body" style={{ fontSize: 17 }}>
            {winner.correct} / {winner.total} correct
            {winner.finished && ` in ${formatTime(winner.finishMs)}`}.
          </p>
        </div>

        {/* Class metrics */}
        {summary && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: 12,
              marginBottom: 32,
            }}
          >
            <SummaryStat
              eyebrow="Class average"
              value={`${summary.avgPercent.toFixed(0)}%`}
              foot={`${summary.avgCorrect.toFixed(1)} of ${summary.total}`}
            />
            <SummaryStat
              eyebrow="Avg finish time"
              value={summary.finishedCount > 0 ? formatTime(summary.avgTimeMs) : '—'}
              foot={`${summary.finishedCount} of ${finalScores.length} finished`}
            />
            <SummaryStat
              eyebrow="Fastest"
              value={summary.fastest ? summary.fastest.playerName : '—'}
              foot={summary.fastest ? formatTime(summary.fastest.finishMs) : 'No one finished'}
            />
            <SummaryStat
              eyebrow="Top score"
              value={`${winner.correct}/${winner.total}`}
              foot={winner.playerName}
            />
          </div>
        )}

        {/* Leaderboard */}
        <section style={{ marginBottom: 36 }}>
          <div className="t-eyebrow" style={{ marginBottom: 12 }}>
            Leaderboard · 排名
          </div>
          <div className="card" style={{ overflow: 'hidden' }}>
            <table className="tbl" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ width: 60 }}>Rank</th>
                  <th>Student</th>
                  <th style={{ width: 100 }}>Score</th>
                  <th style={{ width: 100 }}>Accuracy</th>
                  <th style={{ width: 100 }}>Time</th>
                </tr>
              </thead>
              <tbody>
                {finalScores.map((p, idx) => {
                  const rank = idx + 1;
                  const total = p.total || questions.length || 1;
                  const pct = total > 0 ? (p.correct / total) * 100 : 0;
                  return (
                    <tr key={p.playerId}>
                      <td className="t-mono">
                        {rank === 1 ? '一' : rank === 2 ? '二' : rank === 3 ? '三' : `#${rank}`}
                      </td>
                      <td style={{ fontWeight: 600 }}>{p.playerName}</td>
                      <td className="t-mono">{p.correct}/{total}</td>
                      <td className="t-mono">{pct.toFixed(0)}%</td>
                      <td
                        className="t-mono"
                        style={{ color: !p.finished ? 'var(--vermillion)' : 'inherit' }}
                      >
                        {p.finished ? formatTime(p.finishMs) : 'DNF'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        {/* Per-question review */}
        <section style={{ marginBottom: 36 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              marginBottom: 12,
            }}
          >
            <div className="t-eyebrow">Question review · 题目回顾</div>
            <label
              className="t-small"
              style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
            >
              <input
                type="checkbox"
                checked={showAllPicks}
                onChange={(e) => setShowAllPicks(e.target.checked)}
              />
              Show student picks
            </label>
          </div>
          <p className="t-small" style={{ marginBottom: 12, color: 'var(--ink-3)' }}>
            Click a question to inspect who picked which option. Use this to debrief mistakes with the class.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {questionStats.map((q, i) => {
              const isOpen = expandedQId === q.questionId;
              const correctOption = q.picks.find((o) => o.isCorrect);
              const accuracyPct = q.answeredCount > 0
                ? (q.correctCount / q.answeredCount) * 100
                : 0;
              return (
                <div
                  key={q.questionId}
                  className="card"
                  style={{ padding: 0, overflow: 'hidden' }}
                >
                  <button
                    onClick={() => setExpandedQId(isOpen ? null : q.questionId)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 16,
                      padding: '14px 18px',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontFamily: 'inherit',
                      color: 'inherit',
                    }}
                  >
                    <span
                      className="t-mono"
                      style={{
                        fontSize: 12,
                        color: 'var(--ink-3)',
                        width: 36,
                        flexShrink: 0,
                      }}
                    >
                      Q{String(i + 1).padStart(2, '0')}
                    </span>
                    <span
                      style={{
                        fontFamily: gameType === 'picture' ? 'var(--font-sans)' : 'var(--font-han)',
                        fontSize: gameType === 'picture' ? 14 : 28,
                        lineHeight: 1.1,
                        flexShrink: 0,
                        minWidth: 64,
                      }}
                    >
                      {gameType === 'picture' ? '[image]' : q.hanzi}
                    </span>
                    <span
                      className="t-small"
                      style={{ flex: 1, color: 'var(--ink-2)' }}
                    >
                      ✓ {correctOption?.definition || correctOption?.hanzi || '—'}
                    </span>
                    <span
                      className="t-mono"
                      style={{
                        fontSize: 13,
                        color: accuracyPct >= 75
                          ? 'var(--jade)'
                          : accuracyPct >= 50
                            ? 'var(--ink)'
                            : 'var(--vermillion)',
                        flexShrink: 0,
                      }}
                    >
                      {q.correctCount}/{q.answeredCount} · {accuracyPct.toFixed(0)}%
                    </span>
                    <span
                      className="t-mono"
                      style={{ fontSize: 14, color: 'var(--ink-3)', width: 16, flexShrink: 0 }}
                    >
                      {isOpen ? '−' : '+'}
                    </span>
                  </button>

                  {isOpen && (
                    <div
                      style={{
                        padding: '0 18px 18px',
                        borderTop: '1px solid var(--rule-2)',
                      }}
                    >
                      {q.pinyin && (
                        <p
                          className="pinyin"
                          style={{ fontSize: 14, marginTop: 12, marginBottom: 8 }}
                        >
                          {q.pinyin}
                        </p>
                      )}
                      <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
                        {q.picks.map((opt, idx) => (
                          <div
                            key={opt.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 12,
                              padding: '10px 14px',
                              borderRadius: 8,
                              border: '1px solid var(--rule)',
                              background: opt.isCorrect ? 'rgba(64,140,93,0.10)' : 'var(--paper-2)',
                            }}
                          >
                            <span
                              className="t-mono"
                              style={{
                                fontSize: 12,
                                width: 18,
                                color: opt.isCorrect ? 'var(--jade)' : 'var(--ink-3)',
                              }}
                            >
                              {opt.isCorrect ? '✓' : idx + 1}
                            </span>
                            <span
                              style={{
                                flex: 1,
                                fontWeight: opt.isCorrect ? 600 : 400,
                              }}
                            >
                              {gameType === 'picture' ? opt.hanzi : opt.definition}
                            </span>
                            {showAllPicks && (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                {opt.pickedBy.length === 0 ? (
                                  <span className="t-small" style={{ color: 'var(--ink-3)' }}>—</span>
                                ) : (
                                  opt.pickedBy.map((name) => (
                                    <span
                                      key={name}
                                      className="t-small"
                                      style={{
                                        fontSize: 11,
                                        padding: '2px 8px',
                                        background: opt.isCorrect ? 'var(--jade)' : 'var(--vermillion)',
                                        color: '#fff',
                                        borderRadius: 999,
                                        fontWeight: 500,
                                      }}
                                    >
                                      {name}
                                    </span>
                                  ))
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        <div style={{ display: 'flex', gap: 12 }}>
          <button className="btn btn--ghost" style={{ flex: 1 }} onClick={onBackToMenu}>
            Back to menu
          </button>
          <button className="btn btn--accent" style={{ flex: 2 }} onClick={onPlayAgain}>
            Play again →
          </button>
        </div>
      </div>
    </div>
  );
}

function SummaryStat({ eyebrow, value, foot }) {
  return (
    <div
      className="card"
      style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 4 }}
    >
      <div className="t-eyebrow" style={{ fontSize: 10 }}>{eyebrow}</div>
      <div style={{ fontSize: 24, fontWeight: 600, lineHeight: 1.1 }}>{value}</div>
      {foot && (
        <div className="t-small" style={{ fontSize: 12, color: 'var(--ink-3)' }}>
          {foot}
        </div>
      )}
    </div>
  );
}

export default QuizResultsHost;
